import { DropdownButtonType, List, SingleSelectDropdown } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { generateUniqueId } from '~src/spacesPlatform/utils/DomUtils';

import { Messages } from '../../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../../commonInfra/components/FormikCheckbox';
import { FormikTextBox } from '../../../../../commonInfra/components/FormikField';
import { useFormikContext } from '../../../../FormikWrapperForStubbing';
import { formError, inlineFlexStyle, narrowTextFieldStyle } from '../../../../styles/CommonStyles';
import { DataAccelerationSectionProps } from './DataAccelerationSection';
import { autoSuspendAccelerationImprovementThresholdFieldValidator, autoSuspendFailuresLimitFieldValidator, autoSuspendViewsLimitFieldValidator, getDefaultAccelerationImprovementThresholdValue, getDefaultAutoSuspensionFailureLimitValue, getDefaultAutoSuspensionViewsLimitValue, getLocalizedTimePeriod,getTimePeriodOptions, TimePeriodType } from './DataAccelerationSectionHelper';
import { dropdownListStyle, inLineStyle, inLineStyleWithTopMargin, subHeaderSpacing } from './DataAccelerationSectionStyleHelper';

 // auto suspension failure limit field variables
export const AUTO_SUSPEND_FAILURE_LIMIT_FOR_ACCELERATION_FIELD = 'settings.workbookAccelerationSettings.autoSuspendSettings.autoSuspendFailureLimitForAcceleration';
export const AUTO_SUSPEND_FAILURE_LIMIT_FOR_ACCELERATION_CHECKBOX = 'settings.workbookAccelerationSettings.autoSuspendSettings.autoSuspendFailureLimitForAccelerationEnabled';
export const AUTO_SUSPEND_FAILURE_LIMIT_FOR_ACCELERATION_TIME_PERIOD_FIELD = 'settings.workbookAccelerationSettings.autoSuspendSettings.autoSuspendFailureLimitForAccelerationTimePeriod';

 // auto suspension acceleration improvement threshold field variables
export const AUTO_SUSPEND_ACCELERATION_IMPROVEMENT_THRESHOLD_FIELD = 'settings.workbookAccelerationSettings.autoSuspendSettings.autoSuspendAccelerationImprovementThreshold';
export const AUTO_SUSPEND_ACCELERATION_IMPROVEMENT_THRESHOLD_CHECKBOX = 'settings.workbookAccelerationSettings.autoSuspendSettings.autoSuspendAccelerationImprovementThresholdEnabled';

 // auto suspension views limit field variables
export const AUTO_SUSPEND_VIEWS_LIMIT_FIELD = 'settings.workbookAccelerationSettings.autoSuspendSettings.autoSuspendViewsLimit';
export const AUTO_SUSPEND_VIEWS_LIMIT_CHECKBOX = 'settings.workbookAccelerationSettings.autoSuspendSettings.autoSuspendViewsLimitEnabled';
export const AUTO_SUSPEND_VIEWS_LIMIT_TIME_PERIOD_FIELD = 'settings.workbookAccelerationSettings.autoSuspendSettings.autoSuspendViewsLimitTimePeriod';

export const AUTO_SUSPENSION_FIELDS_DEBOUNCE_MS = 1000;

const failuresIds = [...Array(4)].map(() => generateUniqueId('auto-suspend-failures-limit'));
const improvementIds = [...Array(3)].map(() => generateUniqueId('auto-suspend-acceleration-improvement-threshold'));
const viewsIds = [...Array(4)].map(() => generateUniqueId('auto-suspend-views-limit'));

export function DataAccelerationAutoSuspendSection(props: DataAccelerationSectionProps) {
  const formik = useFormikContext<DataAccelerationSectionProps>();

  // auto suspension failure limit field variables
  const autoSuspendFailureLimitFieldDisabled = !formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendFailureLimitForAccelerationEnabled;
  const autoSuspendFailureLimitValue = !formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendFailureLimitForAcceleration;
  const validateFailureLimit = React.useCallback((value: string) => autoSuspendFailureLimitValue ? null : autoSuspendFailuresLimitFieldValidator(+value), [autoSuspendFailureLimitValue]);
  const autoSuspendFailureLimitForAccelerationError = formik.errors?.settings?.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendFailureLimitForAcceleration;

  // auto suspension acceleration improvement threshold field variables
  const autoSuspendAccelerationImprovementThresholdFieldDisabled = !formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendAccelerationImprovementThresholdEnabled;
  const autoSuspendAccelerationImprovementThreshold = !formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendAccelerationImprovementThreshold;
  const validateAccelerationImprovementThreshold = React.useCallback((value: string) => autoSuspendAccelerationImprovementThreshold ? null : autoSuspendAccelerationImprovementThresholdFieldValidator(+value), [autoSuspendAccelerationImprovementThreshold]);
  const autoSuspendAccelerationImprovementThresholdError = formik.errors?.settings?.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendAccelerationImprovementThreshold;

  // auto suspension views limit field variables
  const autoSuspendViewsLimitFieldDisabled = !formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendViewsLimitEnabled;
  const autoSuspendViewsLimitValue = !formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendViewsLimit;
  const validateViewsLimit = React.useCallback((value: string) => autoSuspendViewsLimitValue ? null : autoSuspendViewsLimitFieldValidator(+value), [autoSuspendViewsLimitValue]);
  const autoSuspendViewsLimitError = formik.errors?.settings?.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendViewsLimit;

  // Don't show the error right away until after the user finishes typing
  // This code was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const SetDebouncedAccelerationFailureFieldTouched = React.useCallback(_.debounce(() => {
    formik.setFieldTouched(AUTO_SUSPEND_FAILURE_LIMIT_FOR_ACCELERATION_FIELD, true);
  }, AUTO_SUSPENSION_FIELDS_DEBOUNCE_MS), [formik.setFieldTouched]);

  // Don't show the error right away until after the user finishes typing
  // This code was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const SetDebouncedAccelerationImprovementThresholdFieldTouched = React.useCallback(_.debounce(() => {
    formik.setFieldTouched(AUTO_SUSPEND_ACCELERATION_IMPROVEMENT_THRESHOLD_FIELD, true);
  }, AUTO_SUSPENSION_FIELDS_DEBOUNCE_MS), [formik.setFieldTouched]);

  // Don't show the error right away until after the user finishes typing
  // This code was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const SetDebouncedViewsLimitFieldTouched = React.useCallback(_.debounce(() => {
    formik.setFieldTouched(AUTO_SUSPEND_VIEWS_LIMIT_FIELD, true);
  }, AUTO_SUSPENSION_FIELDS_DEBOUNCE_MS), [formik.setFieldTouched]);

  const handleChangeForAutoSuspendFailureLimitField = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const autoSuspendFailureLimitCount = (+e.target.value);
      const value = autoSuspendFailuresLimitFieldValidator(autoSuspendFailureLimitCount) === null ? autoSuspendFailureLimitCount : e.target.value;
      formik.setFieldValue(AUTO_SUSPEND_FAILURE_LIMIT_FOR_ACCELERATION_FIELD, value);
      SetDebouncedAccelerationFailureFieldTouched();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.setFieldValue, SetDebouncedAccelerationFailureFieldTouched]
  );

  const handleChangeForAutoSuspendAccelerationImprovementThresholdField = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const autoSuspendAccelerationImprovementThresholdValue = (+e.target.value);
      const value = autoSuspendFailuresLimitFieldValidator(autoSuspendAccelerationImprovementThresholdValue) === null ? autoSuspendAccelerationImprovementThresholdValue : e.target.value;
      formik.setFieldValue(AUTO_SUSPEND_ACCELERATION_IMPROVEMENT_THRESHOLD_FIELD, value);
      SetDebouncedAccelerationImprovementThresholdFieldTouched();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.setFieldValue, SetDebouncedAccelerationImprovementThresholdFieldTouched]
  );

  const handleChangeForAutoSuspendViewsLimitField = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const autoSuspendViewsLimitCount = (+e.target.value);
      const value = autoSuspendViewsLimitFieldValidator(autoSuspendViewsLimitCount) === null ? autoSuspendViewsLimitCount : e.target.value;
      formik.setFieldValue(AUTO_SUSPEND_VIEWS_LIMIT_FIELD, value);
      SetDebouncedViewsLimitFieldTouched();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.setFieldValue, SetDebouncedViewsLimitFieldTouched]
  );

  // Explicitly set viewsLimitPerSiteForAcceleration to undefined when it has error and viewsLimitPerSiteForAcceleration change,
  // so that form validation can happen and form saving can be disabled properly.
  React.useEffect(
    () => {
      const autoSuspendFailureLimitFieldCountError = autoSuspendFailuresLimitFieldValidator(formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendFailureLimitForAcceleration);
      if (autoSuspendFailureLimitFieldCountError) {
        formik.setFieldValue(AUTO_SUSPEND_FAILURE_LIMIT_FOR_ACCELERATION_FIELD, undefined);
      }

      const autoSuspendAccelerationImprovementThresholdError = autoSuspendAccelerationImprovementThresholdFieldValidator(formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendAccelerationImprovementThreshold);
      if (autoSuspendAccelerationImprovementThresholdError) {
        formik.setFieldValue(AUTO_SUSPEND_ACCELERATION_IMPROVEMENT_THRESHOLD_FIELD, undefined);
      }

      const autoSuspendViewsLimitFieldCountError = autoSuspendViewsLimitFieldValidator(formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendViewsLimit);
      if (autoSuspendViewsLimitFieldCountError) {
        formik.setFieldValue(AUTO_SUSPEND_VIEWS_LIMIT_FIELD, undefined);
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoSuspendFailureLimitFieldDisabled, autoSuspendAccelerationImprovementThresholdFieldDisabled, autoSuspendViewsLimitFieldDisabled, formik.setFieldValue]
  );

  const timePeriodDropdownList = <List items={getTimePeriodOptions()} style={dropdownListStyle} />;

  return <>
    {<div>
      {renderAutoSuspendHeader()}
      {/* auto suspend failures limit component */}
      <div>
        <FormikCheckbox name={AUTO_SUSPEND_FAILURE_LIMIT_FOR_ACCELERATION_CHECKBOX}
          label={Messages.autoSuspendFailuresCheckboxText()}
          testId={'auto-suspend-failures-limit-checkbox'}
        >
        <div>
          <FormikTextBox testId={'auto-suspend-failures-limit-textbox'}
            name={AUTO_SUSPEND_FAILURE_LIMIT_FOR_ACCELERATION_FIELD}
            label={Messages.autoSuspendFailuresFieldText()}
            validate={validateFailureLimit}
            showValidationMessage={false}
            disableOnSubmit={true}
            className={narrowTextFieldStyle}
            value={formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendFailureLimitForAcceleration ?? getDefaultAutoSuspensionFailureLimitValue(formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendFailureLimitForAccelerationTimePeriod)}
            maxLength={9}
            required={true}
            onChange={handleChangeForAutoSuspendFailureLimitField}
            disabled={autoSuspendFailureLimitFieldDisabled}
          />
        </div>
        <div>
          <SingleSelectDropdown
            label={Messages.autoSuspendFailuresPerTimePeriod()}
            list={timePeriodDropdownList}
            onChange={(selectedTimePeriod: string) => { formik.setFieldValue(AUTO_SUSPEND_FAILURE_LIMIT_FOR_ACCELERATION_TIME_PERIOD_FIELD, selectedTimePeriod); }}
            type={DropdownButtonType.Outline}
            value={formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendFailureLimitForAccelerationTimePeriod ?? TimePeriodType.Week}
            disabled={autoSuspendFailureLimitFieldDisabled}
            testId={'auto-suspend-failures-limit-time-period-dropdown'}
            className={inlineFlexStyle}
            >
          {getLocalizedTimePeriod(formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendFailureLimitForAccelerationTimePeriod)}
          </SingleSelectDropdown>
        </div>
        </FormikCheckbox>
      </div>
      {
        !autoSuspendFailureLimitFieldDisabled && formik.touched?.settings?.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendFailureLimitForAcceleration &&
        autoSuspendFailureLimitForAccelerationError &&
        <div data-tb-test-id={'auto-suspension-failures-limit-for-acceleration-error'} className={formError}>
          {autoSuspendFailureLimitForAccelerationError}
        </div>
      }
      {/* auto suspend acceleration improvement threshold component */}
      {FeatureFlags.autoSuspendAccelerationImprovementThresholdSetting && <div className={inLineStyle}>
        <FormikCheckbox name={AUTO_SUSPEND_ACCELERATION_IMPROVEMENT_THRESHOLD_CHECKBOX}
          label={''}
          testId={'auto-suspend-acceleration-improvement-threshold-checkbox'}
        />
        &nbsp;
        <FormikTextBox testId={'auto-suspend-acceleration-improvement-threshold-textbox'}
          name={AUTO_SUSPEND_ACCELERATION_IMPROVEMENT_THRESHOLD_FIELD}
          label={null}
          validate={validateAccelerationImprovementThreshold}
          showValidationMessage={false}
          disableOnSubmit={true}
          className={narrowTextFieldStyle}
          value={formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendAccelerationImprovementThreshold ?? getDefaultAccelerationImprovementThresholdValue()}
          maxLength={3}
          required={true}
          onChange={handleChangeForAutoSuspendAccelerationImprovementThresholdField}
          disabled={autoSuspendAccelerationImprovementThresholdFieldDisabled}
        />
      </div>}
      {
        FeatureFlags.autoSuspendAccelerationImprovementThresholdSetting && !autoSuspendAccelerationImprovementThresholdFieldDisabled && formik.touched?.settings?.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendAccelerationImprovementThreshold &&
        autoSuspendAccelerationImprovementThresholdError &&
        <div data-tb-test-id={'auto-suspension-acceleration-improvement-threshold-error'} className={formError}>
          {autoSuspendAccelerationImprovementThresholdError}
        </div>
      }
      {/* auto suspend views limit component */}
      {FeatureFlags.autoSuspendAccelerationViewsLimitSetting && <div className={inLineStyleWithTopMargin}>
        <FormikCheckbox name={AUTO_SUSPEND_VIEWS_LIMIT_CHECKBOX}
          label={''}
          testId={'auto-suspend-views-limit-checkbox'}
        />
        &nbsp;
        <FormikTextBox testId={'auto-suspend-views-limit-textbox'}
          name={AUTO_SUSPEND_VIEWS_LIMIT_FIELD}
          label={null}
          validate={validateViewsLimit}
          showValidationMessage={false}
          disableOnSubmit={true}
          className={narrowTextFieldStyle}
          value={formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendViewsLimit ?? getDefaultAutoSuspensionViewsLimitValue(formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendViewsLimitTimePeriod)}
          maxLength={9}
          required={true}
          onChange={handleChangeForAutoSuspendViewsLimitField}
          disabled={autoSuspendViewsLimitFieldDisabled}
        />
        &nbsp;
        <SingleSelectDropdown
          label={null}
          list={timePeriodDropdownList}
          onChange={(selectedTimePeriod: string) => { formik.setFieldValue(AUTO_SUSPEND_VIEWS_LIMIT_TIME_PERIOD_FIELD, selectedTimePeriod); }}
          type={DropdownButtonType.Text}
          value={formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendViewsLimitTimePeriod ?? TimePeriodType.Week}
          disabled={autoSuspendViewsLimitFieldDisabled}
          testId={'auto-suspend-views-limit-time-period-dropdown'}
        >
          {getLocalizedTimePeriod(formik.values.settings.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendViewsLimitTimePeriod)}
        </SingleSelectDropdown>
      </div>
      }
      {
        FeatureFlags.autoSuspendAccelerationViewsLimitSetting && !autoSuspendViewsLimitFieldDisabled && formik.touched?.settings?.workbookAccelerationSettings?.autoSuspendSettings?.autoSuspendViewsLimit &&
        autoSuspendViewsLimitError &&
        <div data-tb-test-id={'auto-suspension-views-limit-for-acceleration-error'} className={formError}>
          {autoSuspendViewsLimitError}
        </div>
      }
    </div>


    }
  </>;
}

function renderAutoSuspendHeader() {
  return findText(
    Messages.autoSuspendSectionHeader(), 'auto-suspend-header-text');
}

export function findText(text: string, testId: string) {
  return <>
    <span data-tb-test-id={testId} className={subHeaderSpacing}>
      {text}
    </span>
  </>;
}